import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MotifCardModule } from '@ey-xd/ng-motif';
import { ItemLabel, StatsItem } from '@models/items.models';
import { convertToKebabCase, getFormattedEnumKeyByValue, getKebabCaseEnumKeyByValue } from '@utils';

type EnumType = Record<string, string | number>;

@Component({
  selector: 'app-report-pie-chart',
  standalone: true,
  imports: [NgClass, MotifCardModule],
  templateUrl: './report-pie-chart.component.html',
  styleUrl: './report-pie-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportPieChartComponent {
  readonly chartData = input.required<StatsItem[]>();
  readonly statusEnum = input.required<EnumType>();
  readonly sortOrder = input.required<number[]>();

  readonly ordinals = computed(() => {
    const enumLength = Object.values(this.statusEnum()).filter(v => typeof v === 'number').length;
    return Array.from({ length: enumLength }, (_, i) => this.#getOrdinalName(i));
  });

  readonly statuses = computed(() =>
    Object.values(this.statusEnum())
      .filter(value => typeof value === 'number')
      .map(
        (value: number, idx: number): ItemLabel =>
          ({
            key: getFormattedEnumKeyByValue(this.statusEnum(), value),
            value: value,
            class: `donut_label_dot_${this.#getOrdinalName(idx)} donut_label_dot_${getKebabCaseEnumKeyByValue(this.statusEnum(), value) as string}`,
          }) as ItemLabel
      )
      .sort((a, b) => {
        return this.sortOrder().indexOf(a.value) - this.sortOrder().indexOf(b.value);
      })
  );

  public getData = computed(() => {
    const allStatuses = Object.values(this.statusEnum()).filter(value => typeof value === 'number');
    return allStatuses
      .map(status => {
        const existing = this.chartData()?.find(item => item.status === status);
        return existing?.count
          ? { ...existing, displayName: getFormattedEnumKeyByValue(this.statusEnum(), status) }
          : { status: status, count: 0, displayName: getFormattedEnumKeyByValue(this.statusEnum(), status) };
      })
      .sort((a, b) => b.count - a.count);
  });

  public readonly totalCount = computed(() => this.getData().reduce((sum, item) => sum + item.count, 0));

  public readonly chartAriaLabel = computed(() => {
    const values = this.getData().map(item => `${item.displayName}: ${item.count}`).join('. ');
    return `Donut chart showing status distribution. ${values}.`;
  });

  public getSliceClass(ordinal: string): string {
    const index = this.ordinals().indexOf(ordinal);
    const displayName = convertToKebabCase(this.getData()[index]?.displayName as string) || '';
    return `donut__slice__${ordinal} donut__slice__${displayName}`;
  }

  getStyleProperty(): string {
    const data = this.getData();
    const total = this.totalCount();
    let cumulativeSum = 0;

    return data
      .map((item, index) => {
        let value = (item.count / total).toFixed(6);
        if (index === data.length - 1) {
          value = (1 - cumulativeSum).toFixed(6);
        }
        cumulativeSum += parseFloat(value);
        const ordinal = this.#getOrdinalName(index);
        const ordinalValue = `--${ordinal}: ${value};`;
        const name = convertToKebabCase(item.displayName as string);
        const nameValue = `--${name}: ${value};`;
        return `${ordinalValue} ${nameValue}`;
      })
      .join(' ');
  }

  #getOrdinalName(n: number): string {
    const ordinals = [
      'first',
      'second',
      'third',
      'fourth',
      'fifth',
      'sixth',
      'seventh',
      'eighth',
      'ninth',
      'tenth',
      'eleventh',
      'twelfth',
      'thirteenth',
      'fourteenth',
      'fifteenth',
    ];
    return ordinals[n] || `ordinal${n + 1}`;
  }

  getLegendItemClass(status: ItemLabel) {
    return status.class;
  }

  getLegendText(status: ItemLabel) {
    return status.key.length > 12 ? `${status.key.slice(0, 12)}.` : status.key;
  }

  getAmount(status: ItemLabel) {
    return this.getData().find(item => item.displayName === status.key)?.count || 0;
  }

  canShowSlice(item: string) {
    const r = this.getData()[this.ordinals().indexOf(item)]?.count > 0;
    return r;
  }
}
